import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { of } from 'rxjs';
import { timeout, catchError, finalize } from 'rxjs/operators';
import { ShaclPropertyShape, ShaclNodeShape } from '../../../models/ShaclShapeSummary';
import { ShaclApiService } from '../../../services/shacl-api.service';
import { EffectivePropertyDTO } from '../../../models/EffectivePropertyDTO';

@Component({
  selector: 'app-form-field',
  standalone: false,
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss']
})
export class FormFieldComponent implements OnInit, OnChanges {
  @Input() field: any;
  @Input() formGroup!: FormGroup;
  @Input() parentPath = '';
  @Input() initialValue: any;
  @Input() parentShapes: string[] = [];

  nestedShape?: ShaclNodeShape;
  loadingNestedShape = false;
  nestedShapeError?: string;
  selectedRegistrationTypes: { [key: number]: string } = {}; // For arrays: index -> type
  selectedSingleRegistrationType = ''; // For non-arrays

  registrationOptions = [
    { label: 'vatID', value: 'https://w3id.org/gaia-x/development#vatID' },
    { label: 'EUID', value: 'https://w3id.org/gaia-x/development#euid' },
    { label: 'EORI', value: 'https://w3id.org/gaia-x/development#eori' },
    { label: 'LEI Code', value: 'https://schema.org/leiCode' }
  ];

  constructor(private api: ShaclApiService) { }

  get normalizedField(): EffectivePropertyDTO {
    const f = this.field as any;
    // Enhanced detection: if path exists and we have some property that indicates it's a DTO
    const isNewDto = f && f.path && (f.required !== undefined || f.allowedTypes !== undefined || f.in !== undefined || f.inValues !== undefined);

    if (isNewDto) {
      return {
        path: f.path,
        required: !!f.required,
        minCount: f.minCount !== undefined ? f.minCount : (f.required ? 1 : 0),
        maxCount: f.maxCount !== undefined ? f.maxCount : null,
        description: f.description ?? null,
        name: f.name ?? null,
        clazz: f.clazz ?? null,
        nestedRequired: f.nestedRequired ?? [],
        allowedTypes: f.allowedTypes ?? f.inValues ?? f.in ?? []
      };
    }
    const maxCount = typeof f.maxCount === 'number' ? f.maxCount : null;
    const required = !!f.required || ((typeof f.minCount === 'number' ? f.minCount : 0) > 0);
    return {
      path: f.path || '',
      required,
      minCount: f.minCount !== undefined ? f.minCount : (required ? 1 : 0),
      maxCount,
      description: f.description || '',
      name: f.name || '',
      clazz: f.clazz || '',
      nestedRequired: [],
      allowedTypes: Array.isArray(f.inValues) ? f.inValues : []
    };
  }

  get fieldLabel(): string {
    const nf = this.normalizedField;
    const path = nf.path || '';
    const lastPart = path.split(/[#/]/).pop() || '';
    return (nf.name && nf.name.trim().length > 0) ? nf.name : lastPart;
  }

  get fieldControlName(): string {
    const full = this.parentPath ? `${this.parentPath}::${this.normalizedField.path}` : this.normalizedField.path;
    return full.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/_{2,}/g, '_');
  }

  get fullPath(): string {
    return this.parentPath ? `${this.parentPath}.${this.fieldLabel}` : this.fieldLabel;
  }

  get isArray(): boolean {
    const mc = this.normalizedField.maxCount;
    return mc === null || (mc ?? 0) > 1;
  }

  get minCount(): number {
    return this.normalizedField.minCount;
  }

  get canAddMore(): boolean {
    if (!this.isArray) return false;
    const maxCount = this.normalizedField.maxCount ?? Infinity;
    return (this.formArray?.length ?? 0) < maxCount;
  }

  get canRemove(): boolean {
    return this.isArray && (this.formArray?.length ?? 0) > (this.normalizedField.required ? 1 : 0);
  }

  get formArray(): FormArray | null {
    const ctrl = this.formGroup.get(this.fieldControlName);
    return ctrl instanceof FormArray ? ctrl : null;
  }

  get isComplex(): boolean {
    return !!this.normalizedField.clazz;
  }

  get isRegistrationNumber(): boolean {
    return this.normalizedField.clazz === 'https://w3id.org/gaia-x/development#RegistrationNumber';
  }

  get inputType(): string {
    const label = (this.fieldLabel || '').toLowerCase();
    const path = (this.normalizedField.path || '').toLowerCase();
    if (label.includes('is ') || label.startsWith('is') || path.endsWith('boolean')) return 'checkbox';
    if (path.includes('amount') || path.includes('number') || path.endsWith('count')) return 'number';
    if (path.endsWith('date') || label.includes('date')) return 'date';
    return 'text';
  }

  get hasEnum(): boolean {
    return !!this.normalizedField.allowedTypes && this.normalizedField.allowedTypes.length > 0 && !this.isComplex;
  }

  getControl(name: string): FormControl | null {
    if (!this.formGroup) {
      return null;
    }
    const ctrl = this.formGroup.get(name);
    if (!ctrl) {
    }
    return ctrl instanceof FormControl ? ctrl : null;
  }

  getArrayItemControl(index: number): FormControl | null {
    const arr = this.formArray;
    if (!arr) {
      return null;
    }
    const ctrl = arr.at(index);
    return ctrl instanceof FormControl ? ctrl : null;
  }

  ngOnInit(): void {
    this.initializeControl();
    if (this.isComplex) this.loadNestedShape();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValue']) {
      this.updateControlValue();
    }
  }

  private updateControlValue(): void {
    const key = this.fieldControlName;
    if (!this.formGroup) return;

    if (!this.isArray && !this.isComplex) {
      const ctrl = this.formGroup.get(key);
      if (ctrl && this.initialValue !== undefined && (!ctrl.value || ctrl.value === '')) {
        ctrl.setValue(this.initialValue);
      }
    }
  }

  private initializeControl(): void {
    const key = this.fieldControlName;
    if (!this.formGroup) return;

    if (this.isArray) {
      let arr = this.formGroup.get(key) as FormArray;
      if (!(arr instanceof FormArray)) {
        arr = new FormArray([]);
        this.formGroup.setControl(key, arr);
      }

      // If we have initial array values represented as an array in initialValue
      if (arr.length === 0 && Array.isArray(this.initialValue)) {
        this.initialValue.forEach((v: any) => {
          arr.push(this.isComplex ? new FormGroup({}) : new FormControl(v));
        });
      }

      // If required but still empty, add initial item
      if (arr.length === 0 && this.normalizedField.required) {
        const defaultValue = (this.initialValue && !Array.isArray(this.initialValue)) ? this.initialValue : '';
        arr.push(this.isComplex ? new FormGroup({}) : new FormControl(defaultValue));
      }

      // Apply validators to the array itself if needed
      if (this.normalizedField.required && arr.validator === null) {
        arr.setValidators([Validators.minLength(1)]);
        arr.updateValueAndValidity();
      }
    } else if (this.isComplex) {
      if (!(this.formGroup.get(key) instanceof FormGroup)) {
        this.formGroup.setControl(key, new FormGroup({}));
      }
    } else {
      if (!this.formGroup.get(key)) {
        const val = this.initialValue !== undefined ? this.initialValue : '';
        this.formGroup.addControl(key, new FormControl(val, this.normalizedField.required ? [Validators.required] : []));
      }
    }
  }



  private loadNestedShape(): void {
    const iri = this.normalizedField.clazz || this.normalizedField.path;
    if (!iri) return;

    // Detect recursion
    if (this.parentShapes.includes(iri)) {
      this.nestedShapeError = 'Circular reference detected';
      return;
    }

    this.loadingNestedShape = true;
    this.api.getShape(iri).pipe(
      timeout(15000),
      catchError(() => of(null)),
      finalize(() => this.loadingNestedShape = false)
    ).subscribe(shape => {
      if (shape) {
        this.nestedShape = shape as unknown as ShaclNodeShape;
        this.initializeNestedControls();
      }
    });
  }

  private initializeNestedControls(): void {
    if (!this.nestedShape) return;
    const fields = this.nestedShape.fields || this.nestedShape.properties || [];
    if (this.isArray) {
      this.formArray?.controls.forEach((control, index) => {
        if (control instanceof FormGroup) {
          fields.forEach(f => {
            const key = this.makeNestedKey(f.path, index);
            if (!control.get(key)) {
              const nestedVal = this.getNestedInitialValue(f, this.initialValue ? this.initialValue[index] : undefined);
              control.addControl(key, this.createNestedControl(f, nestedVal));
            }
          });
          // Also check for Registration Number values if it's one
          if (this.isRegistrationNumber) {
            this.registrationOptions.forEach(opt => {
              const key = this.makeNestedKey(opt.value, index);
              if (control.get(key) && control.get(key)?.value) {
                this.selectedRegistrationTypes[index] = opt.value;
              }
            });
          }
        }
      });
    } else {
      const group = this.formGroup.get(this.fieldControlName);
      if (group instanceof FormGroup) {
        fields.forEach(f => {
          const key = this.makeNestedKey(f.path);
          if (!group.get(key)) {
            const nestedVal = this.getNestedInitialValue(f, this.initialValue);
            group.addControl(key, this.createNestedControl(f, nestedVal));
          }
        });
        // Also check for Registration Number values
        if (this.isRegistrationNumber) {
          this.registrationOptions.forEach(opt => {
            const key = this.makeNestedKey(opt.value);
            if (group.get(key) && group.get(key)?.value) {
              this.selectedSingleRegistrationType = opt.value;
            }
          });
        }
      }
    }
  }

  onRegistrationTypeChange(type: string, index?: number): void {
    const group = index !== undefined ? this.getNestedGroupFromArray(index) : this.getNestedFormGroup();
    if (!group) return;

    if (index !== undefined) {
      this.selectedRegistrationTypes[index] = type;
    } else {
      this.selectedSingleRegistrationType = type;
    }

    // Ensure the control exists in the group
    const key = this.makeNestedKey(type, index);
    if (!group.get(key)) {
      group.addControl(key, new FormControl('', [Validators.required]));
    }

    // Clear other registration controls to prevent them from blocking form validity
    this.registrationOptions.forEach(opt => {
      if (opt.value !== type) {
        const otherKey = this.makeNestedKey(opt.value, index);
        const otherCtrl = group.get(otherKey);
        if (otherCtrl) {
          otherCtrl.setValue('');
          otherCtrl.clearValidators();
          otherCtrl.updateValueAndValidity();
        }
      } else {
        // Ensure active control has required validator
        const activeCtrl = group.get(key);
        if (activeCtrl) {
          activeCtrl.setValidators([Validators.required]);
          activeCtrl.updateValueAndValidity();
        }
      }
    });
  }

  getRegistrationControlName(type: string, index?: number): string {
    return this.makeNestedKey(type, index);
  }

  getSelectedRegistrationLabel(index?: number): string {
    const value = index !== undefined ? this.selectedRegistrationTypes[index] : this.selectedSingleRegistrationType;
    return this.registrationOptions.find(o => o.value === value)?.label || 'Value';
  }

  private makeNestedKey(path: string, index?: number): string {
    const parent = index !== undefined ? `${this.fullPath}[${index}]` : this.fullPath;
    const full = `${parent}::${path}`;
    return full.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').replace(/_{2,}/g, '_');
  }

  private createNestedControl(field: any, initialValue?: any): FormControl | FormGroup {
    const required = field.required || (field.minCount !== undefined && field.minCount > 0);
    const val = initialValue !== undefined ? initialValue : '';
    return field.clazz ? new FormGroup({}) : new FormControl(val, required ? [Validators.required] : []);
  }

  getNestedFields(): ShaclPropertyShape[] {
    const fields = this.nestedShape?.fields || this.nestedShape?.properties || [];
    return [...fields].sort((a, b) => ((a as any).order || 0) - ((b as any).order || 0));
  }

  getNestedFormGroup(): FormGroup | null {
    const ctrl = this.formGroup.get(this.fieldControlName);
    return ctrl instanceof FormGroup ? ctrl : null;
  }

  getNestedGroupFromArray(index: number): FormGroup | null {
    const ctrl = this.formArray?.at(index);
    return ctrl instanceof FormGroup ? ctrl : null;
  }

  addArrayItem(): void {
    if (!this.canAddMore || !this.formArray) return;
    const control = this.isComplex ? new FormGroup({}) : new FormControl('');
    this.formArray.push(control);
    if (this.isComplex && this.nestedShape) this.initializeNestedControls();
  }

  removeArrayItem(index: number): void {
    if (this.canRemove) this.formArray?.removeAt(index);
  }

  getNestedInitialValue(field: ShaclPropertyShape, parentValue: any): any {
    if (!parentValue || typeof parentValue !== 'object') return undefined;

    const path = (field.path || '').toLowerCase();
    const label = (field.name || '').toLowerCase();

    const segments = path.split(/[#/]/);
    const lastSegment = segments[segments.length - 1].replace(/[^a-z]/g, '');

    for (const key of Object.keys(parentValue)) {
      const lKey = key.toLowerCase().replace(/[^a-z]/g, '');
      if (lastSegment === lKey || path.includes(key.toLowerCase()) || label.includes(key.toLowerCase())) {
        return parentValue[key];
      }
    }
    return undefined;
  }

  get currentShapes(): string[] {
    const iri = this.normalizedField.clazz || this.normalizedField.path;
    return iri ? [...this.parentShapes, iri] : this.parentShapes;
  }
}